import React, { useEffect, useId, useRef, useState } from 'react';
import {
  FaLightbulb,
  FaTimes,
  FaChevronDown,
  FaBook,
  FaChartBar,
  FaRocket,
  FaCrown,
  FaPaperPlane,
  FaCloudUploadAlt,
  FaLeaf,
} from 'react-icons/fa';
import { useCloseOnEscape } from '../hooks/useCloseOnEscape.ts';
import './css/SuggestTopicModal.css';

const TITLE_MAX = 80;
const DESC_MAX = 500;
const FILE_MAX_BYTES = 10 * 1024 * 1024;
const ALLOWED_EXTENSIONS = ['.pdf', '.png', '.jpg', '.jpeg'];
const ALLOWED_MIME_TYPES = new Set(['application/pdf', 'image/png', 'image/jpeg']);

type KnowledgeLevel = 'beginner' | 'basic' | 'intermediate' | 'advanced' | 'expert';
type TaskDifficulty = 'easy' | 'medium' | 'hard';

type CategoryOption = { id: string; label: string };

type ModalCopy = {
  title: string;
  subtitle: string;
  close: string;
  fieldTitle: string;
  fieldTitlePlaceholder: string;
  fieldCategory: string;
  fieldCategoryPlaceholder: string;
  fieldDifficulty: string;
  fieldDescription: string;
  fieldDescriptionPlaceholder: string;
  fieldKnowledge: string;
  fieldMaterials: string;
  fieldMaterialsHint: string;
  fieldMaterialsSub: string;
  agreeLabel: string;
  cancel: string;
  submit: string;
  errorTitle: string;
  errorDescription: string;
  errorAgree: string;
  errorFileType: string;
  errorFileSize: string;
  levels: Record<KnowledgeLevel, string>;
  difficulties: Record<TaskDifficulty, string>;
};

const COPY: Record<'ru' | 'en', ModalCopy> = {
  ru: {
    title: 'Предложить тему',
    subtitle:
      'Есть интересная тема, которой пока нет на Sciplex? Предложите её — мы обязательно рассмотрим!',
    close: 'Закрыть',
    fieldTitle: 'Название темы',
    fieldTitlePlaceholder: 'Например: Алгоритм Форда–Фалкерсона',
    fieldCategory: 'Категория',
    fieldCategoryPlaceholder: 'Выберите категорию',
    fieldDifficulty: 'Тип задачи',
    fieldDescription: 'Описание темы',
    fieldDescriptionPlaceholder:
      'Расскажите, о чём эта тема и почему она будет полезна сообществу',
    fieldKnowledge: 'Ваш уровень знаний в этой теме',
    fieldMaterials: 'Дополнительные материалы',
    fieldMaterialsHint: 'Перетащите файл сюда или нажмите для выбора',
    fieldMaterialsSub: 'PNG, JPEG или PDF — до 10 МБ',
    agreeLabel: 'Я согласен с тем, что моя тема может быть отредактирована командой Sciplex',
    cancel: 'Отмена',
    submit: 'Отправить предложение',
    errorTitle: 'Укажите название темы',
    errorDescription: 'Добавьте описание темы',
    errorAgree: 'Подтвердите согласие на редактирование',
    errorFileType: 'Допустимы только PNG, JPEG и PDF',
    errorFileSize: 'Файл не должен превышать 10 МБ',
    levels: {
      beginner: 'Новичок',
      basic: 'Базовый',
      intermediate: 'Средний',
      advanced: 'Продвинутый',
      expert: 'Эксперт',
    },
    difficulties: {
      easy: 'Легкий',
      medium: 'Средний',
      hard: 'Сложный',
    },
  },
  en: {
    title: 'Suggest a topic',
    subtitle:
      "Have an interesting topic that's not on Sciplex yet? Suggest it — we'll definitely review it!",
    close: 'Close',
    fieldTitle: 'Topic name',
    fieldTitlePlaceholder: 'For example: Ford–Fulkerson algorithm',
    fieldCategory: 'Category',
    fieldCategoryPlaceholder: 'Select a category',
    fieldDifficulty: 'Task type',
    fieldDescription: 'Topic description',
    fieldDescriptionPlaceholder:
      'Tell us what the topic is about and why it would help the community',
    fieldKnowledge: 'Your knowledge level in this topic',
    fieldMaterials: 'Additional materials',
    fieldMaterialsHint: 'Drag a file here or click to browse',
    fieldMaterialsSub: 'PNG, JPEG or PDF — up to 10 MB',
    agreeLabel: 'I agree that my topic may be edited by the Sciplex team',
    cancel: 'Cancel',
    submit: 'Submit suggestion',
    errorTitle: 'Enter a topic name',
    errorDescription: 'Add a topic description',
    errorAgree: 'Confirm the editing agreement',
    errorFileType: 'Only PNG, JPEG, and PDF are allowed',
    errorFileSize: 'File must be 10 MB or smaller',
    levels: {
      beginner: 'Beginner',
      basic: 'Basic',
      intermediate: 'Intermediate',
      advanced: 'Advanced',
      expert: 'Expert',
    },
    difficulties: {
      easy: 'Easy',
      medium: 'Medium',
      hard: 'Hard',
    },
  },
};

const LEVEL_OPTIONS: { id: KnowledgeLevel; icon: React.ReactNode }[] = [
  { id: 'beginner', icon: <FaLeaf aria-hidden /> },
  { id: 'basic', icon: <FaBook aria-hidden /> },
  { id: 'intermediate', icon: <FaChartBar aria-hidden /> },
  { id: 'advanced', icon: <FaRocket aria-hidden /> },
  { id: 'expert', icon: <FaCrown aria-hidden /> },
];

const DIFFICULTY_OPTIONS: TaskDifficulty[] = ['easy', 'medium', 'hard'];

function isAllowedFile(file: File): boolean {
  const name = file.name.toLowerCase();
  const byExtension = ALLOWED_EXTENSIONS.some((ext) => name.endsWith(ext));
  const byMime = file.type ? ALLOWED_MIME_TYPES.has(file.type) : false;
  return byExtension || byMime;
}

type SuggestTopicModalProps = {
  open: boolean;
  onClose: () => void;
  categories: CategoryOption[];
  language: 'ru' | 'en';
};

export function SuggestTopicModal({
  open,
  onClose,
  categories,
  language,
}: SuggestTopicModalProps) {
  const copy = COPY[language];
  const formId = useId();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('');
  const [taskDifficulty, setTaskDifficulty] = useState<TaskDifficulty>('easy');
  const [description, setDescription] = useState('');
  const [knowledgeLevel, setKnowledgeLevel] = useState<KnowledgeLevel>('beginner');
  const [file, setFile] = useState<File | null>(null);
  const [agreed, setAgreed] = useState(true);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useCloseOnEscape(open && !submitting, onClose);

  useEffect(() => {
    if (!open) return;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  const resetForm = () => {
    setTitle('');
    setCategory('');
    setTaskDifficulty('easy');
    setDescription('');
    setKnowledgeLevel('beginner');
    setFile(null);
    setAgreed(true);
    setError(null);
    setSubmitting(false);
    setDragOver(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleClose = () => {
    if (submitting) return;
    resetForm();
    onClose();
  };

  const applyFile = (next: File | null) => {
    if (!next) {
      setFile(null);
      return;
    }
    if (!isAllowedFile(next)) {
      setError(copy.errorFileType);
      return;
    }
    if (next.size > FILE_MAX_BYTES) {
      setError(copy.errorFileSize);
      return;
    }
    setError(null);
    setFile(next);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const titleTrim = title.trim();
    const descTrim = description.trim();

    if (!titleTrim) {
      setError(copy.errorTitle);
      return;
    }
    if (!descTrim) {
      setError(copy.errorDescription);
      return;
    }
    if (!agreed) {
      setError(copy.errorAgree);
      return;
    }

    setSubmitting(true);
    await new Promise((resolve) => window.setTimeout(resolve, 600));
    setSubmitting(false);
    resetForm();
    onClose();
  };

  if (!open) return null;

  return (
    <div
      className="suggest-topic-overlay"
      onClick={handleClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby={`${formId}-title`}
    >
      <div className="suggest-topic-modal" onClick={(e) => e.stopPropagation()}>
        <div className="suggest-topic-modal-head">
          <div className="suggest-topic-modal-head-main">
            <span className="suggest-topic-modal-icon" aria-hidden>
              <FaLightbulb />
            </span>
            <div>
              <h2 id={`${formId}-title`} className="suggest-topic-modal-title">
                {copy.title}
              </h2>
              <p className="suggest-topic-modal-subtitle">{copy.subtitle}</p>
            </div>
          </div>
          <button
            type="button"
            className="suggest-topic-modal-close"
            onClick={handleClose}
            aria-label={copy.close}
          >
            <FaTimes aria-hidden />
          </button>
        </div>

        <form className="suggest-topic-form" onSubmit={handleSubmit}>
          <div className="suggest-topic-field">
            <label htmlFor={`${formId}-title-input`} className="suggest-topic-label">
              <span className="suggest-topic-label-line">
                {copy.fieldTitle}
                <span className="suggest-topic-required" aria-hidden="true">*</span>
              </span>
            </label>
            <div className="suggest-topic-input-wrap">
              <input
                id={`${formId}-title-input`}
                type="text"
                className="suggest-topic-input"
                placeholder={copy.fieldTitlePlaceholder}
                value={title}
                maxLength={TITLE_MAX}
                onChange={(e) => setTitle(e.target.value)}
              />
              <span className="suggest-topic-counter">
                {title.length}/{TITLE_MAX}
              </span>
            </div>
          </div>

          <div className="suggest-topic-field">
            <label htmlFor={`${formId}-category`} className="suggest-topic-label">
              {copy.fieldCategory}
            </label>
            <div className="suggest-topic-select-wrap">
              <select
                id={`${formId}-category`}
                className="suggest-topic-select"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
              >
                <option value="">{copy.fieldCategoryPlaceholder}</option>
                {categories.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.label}
                  </option>
                ))}
              </select>
              <FaChevronDown className="suggest-topic-select-icon" aria-hidden />
            </div>
          </div>

          <div className="suggest-topic-field">
            <span className="suggest-topic-label">{copy.fieldDifficulty}</span>
            <div className="suggest-topic-difficulties" role="group" aria-label={copy.fieldDifficulty}>
              {DIFFICULTY_OPTIONS.map((id) => (
                <button
                  key={id}
                  type="button"
                  className={`suggest-topic-difficulty suggest-topic-difficulty--${id}${
                    taskDifficulty === id ? ' suggest-topic-difficulty--active' : ''
                  }`}
                  onClick={() => setTaskDifficulty(id)}
                  aria-pressed={taskDifficulty === id}
                >
                  {copy.difficulties[id]}
                </button>
              ))}
            </div>
          </div>

          <div className="suggest-topic-field">
            <label htmlFor={`${formId}-description`} className="suggest-topic-label">
              <span className="suggest-topic-label-line">
                {copy.fieldDescription}
                <span className="suggest-topic-required" aria-hidden="true">*</span>
              </span>
            </label>
            <div className="suggest-topic-textarea-wrap">
              <textarea
                id={`${formId}-description`}
                className="suggest-topic-textarea"
                placeholder={copy.fieldDescriptionPlaceholder}
                value={description}
                maxLength={DESC_MAX}
                rows={4}
                onChange={(e) => setDescription(e.target.value)}
              />
              <span className="suggest-topic-counter suggest-topic-counter--textarea">
                {description.length}/{DESC_MAX}
              </span>
            </div>
          </div>

          <div className="suggest-topic-field">
            <span className="suggest-topic-label">{copy.fieldKnowledge}</span>
            <div className="suggest-topic-levels" role="group" aria-label={copy.fieldKnowledge}>
              {LEVEL_OPTIONS.map(({ id, icon }) => (
                <button
                  key={id}
                  type="button"
                  className={`suggest-topic-level${
                    knowledgeLevel === id ? ' suggest-topic-level--active' : ''
                  }`}
                  onClick={() => setKnowledgeLevel(id)}
                  aria-pressed={knowledgeLevel === id}
                >
                  <span className="suggest-topic-level-icon">{icon}</span>
                  <span>{copy.levels[id]}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="suggest-topic-field">
            <span className="suggest-topic-label">{copy.fieldMaterials}</span>
            <div
              className={`suggest-topic-dropzone${dragOver ? ' is-dragover' : ''}${
                file ? ' has-file' : ''
              }`}
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(false);
                const dropped = e.dataTransfer.files?.[0];
                if (dropped) applyFile(dropped);
              }}
              onClick={() => fileInputRef.current?.click()}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  fileInputRef.current?.click();
                }
              }}
              role="button"
              tabIndex={0}
            >
              <FaCloudUploadAlt className="suggest-topic-dropzone-icon" aria-hidden />
              <p className="suggest-topic-dropzone-text">
                {file ? file.name : copy.fieldMaterialsHint}
              </p>
              <p className="suggest-topic-dropzone-sub">{copy.fieldMaterialsSub}</p>
              <input
                ref={fileInputRef}
                type="file"
                className="suggest-topic-file-input"
                accept="image/png,image/jpeg,application/pdf,.png,.jpg,.jpeg,.pdf"
                onChange={(e) => applyFile(e.target.files?.[0] ?? null)}
              />
            </div>
          </div>

          <label className="suggest-topic-agree">
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
            />
            <span className="suggest-topic-agree-box" aria-hidden />
            <span className="suggest-topic-agree-text">{copy.agreeLabel}</span>
          </label>

          {error && (
            <p className="suggest-topic-error" role="alert">
              {error}
            </p>
          )}

          <div className="suggest-topic-actions">
            <button
              type="button"
              className="suggest-topic-btn suggest-topic-btn--ghost"
              onClick={handleClose}
              disabled={submitting}
            >
              {copy.cancel}
            </button>
            <button
              type="submit"
              className="suggest-topic-btn suggest-topic-btn--primary"
              disabled={submitting}
            >
              {copy.submit}
              <FaPaperPlane aria-hidden />
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
